import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { getDatabase, ref, onValue, update, get } from 'firebase/database';
import { app } from '../firebase';
import bloomingdalesLogo from '../assets/bloomingdales-logo-png-transparent.png';
import beachImage from '../assets/beach.jpg';
import anthropologieLogo from '../assets/anthropologie-logo-vector.svg';
import PurchaseModal from '../components/PurchaseModal';

const PageContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
`;

const Title = styled.h1`
  text-align: center;
  margin-bottom: 2rem;
  color: #333;
  font-family: 'Lora', serif;
  font-weight: 500;
`;

const RegistryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  margin-top: 2rem;
`;

const RegistryCard = styled.a`
  display: block;
  text-decoration: none;
  background: white;
  border-radius: 10px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
  overflow: hidden;
  transition: transform 0.3s ease;
  height: 100%;

  &:hover {
    transform: translateY(-5px);
  }
`;

const RegistryImage = styled.div`
  height: 200px;
  background-position: center;
  background-repeat: no-repeat;
  background-color: #fff;
  padding: ${props => props.noPadding ? '0' : '2rem'};
  display: flex;
  align-items: center;
  justify-content: center;
  
  img {
    width: 100%;
    height: ${props => props.coverImage ? '100%' : 'auto'};
    object-fit: ${props => props.coverImage ? 'cover' : 'contain'};
  }
`;

const RegistryInfo = styled.div`
  padding: 1.5rem;
  text-align: center;
  border-top: 1px solid #eee;
`;

const RegistryName = styled.h3`
  margin: 0;
  color: #333;
  font-family: 'Lora', serif;
  font-size: 1.2rem;
  margin-bottom: 0.5rem;
`;

const RegistryDescription = styled.p`
  color: #666;
  margin: 0.5rem 0;
  font-size: 0.9rem;
  line-height: 1.4;
`;

const IntroText = styled.p`
  text-align: center;
  font-size: 1.2rem;
  color: #666;
  margin-bottom: 3rem;
  max-width: 840px;
  margin-left: auto;
  margin-right: auto;
`;

const CustomRegistrySection = styled.div`
  margin-top: 4rem;
  padding-top: 4rem;
  border-top: 1px solid #eee;
`;

const CustomRegistryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 1.5rem;
  margin-top: 2rem;
`;

const CustomRegistryItem = styled.div`
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  padding: 1.5rem;
  cursor: pointer;
  transition: transform 0.3s ease;
  position: relative;
  overflow: hidden;

  ${props => props.purchased && `
    &:after {
      content: 'Purchased';
      position: absolute;
      top: 1rem;
      right: -2rem;
      background: #4CAF50;
      color: white;
      padding: 0.25rem 3rem;
      transform: rotate(45deg);
      font-size: 0.8rem;
    }
  `}

  &:hover {
    transform: translateY(-3px);
  }
`;

const ItemTitle = styled.h3`
  margin: 0;
  color: #333;
  font-family: 'Lora', serif;
  font-size: 1.4rem;
  font-weight: 500;
  margin-bottom: 0.5rem;
  display: flex;
  justify-content: center;
`;

const ItemDescription = styled.p`
  color: #666;
  margin: 0.5rem 0;
  font-family: 'EB Garamond', serif;
  font-size: 1.2rem;
  line-height: 1.6;
`;

const SectionTitle = styled.h2`
  text-align: center;
  margin-bottom: 1.5rem;
  color: #333;
  font-family: 'Lora', serif;
  font-weight: 500;
`;

const Description = styled.p`
  text-align: center;
  margin-bottom: 3rem;
  color: #666;
  max-width: 840px;
  margin-left: auto;
  margin-right: auto;
  font-family: 'EB Garamond', serif;
  font-size: 1.2rem;
  line-height: 1.6;
`;

const PurchaseButton = styled.button`
  padding: 0.5rem 1rem;
  background: #333;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-family: 'EB Garamond', serif;
  font-size: 1.1rem;
  transition: background-color 0.2s;

  &:hover {
    background: #444;
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
`;

const PurchasedText = styled.span`
  color: #4caf50;
  font-family: 'EB Garamond', serif;
  font-size: 1.1rem;
`;

const Registry = () => {
  const [customItems, setCustomItems] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    const fetchItems = async () => {
      const db = getDatabase(app);
      const itemsRef = ref(db, 'registryItems');
      
      try {
        // Initial fetch
        const snapshot = await get(itemsRef);
        if (snapshot.exists()) {
          const items = [];
          snapshot.forEach((childSnapshot) => {
            items.push({
              id: childSnapshot.key,
              ...childSnapshot.val()
            });
          });
          setCustomItems(items);
        }

        // Set up real-time listener
        const unsubscribe = onValue(itemsRef, (snapshot) => {
          if (snapshot.exists()) {
            const items = [];
            snapshot.forEach((childSnapshot) => {
              items.push({
                id: childSnapshot.key,
                ...childSnapshot.val()
              });
            });
            setCustomItems(items);
          }
        });

        return () => unsubscribe();
      } catch (error) {
        console.error('Error fetching registry items:', error);
      }
    };

    fetchItems();
  }, []);

  const handlePurchase = async (itemId) => {
    const db = getDatabase(app);
    const updates = {};
    updates[`/registryItems/${itemId}/purchased`] = true;
    
    try {
      await update(ref(db), updates);
      setIsModalOpen(false);
    } catch (error) {
      console.error('Error updating purchase status:', error);
    }
  };

  const registries = [
    {
      name: "Bloomingdale's Wedding Registry",
      description: "View our wedding registry at Bloomingdale's",
      image: bloomingdalesLogo,
      url: "https://www.bloomingdales.com/registry/Hana-Schwartz-David-Pochapin/1063067",
      backgroundColor: "#fff"
    },
    {
      name: "Anthropologie Registry",
      description: "View our wedding registry at Anthropologie",
      image: anthropologieLogo,
      url: "https://www.anthropologie.com/registry/listing?registryId=F54817147F3A",
      backgroundColor: "#fff",
      noPadding: true
    },
    {
      name: "Honeyfund",
      description: "Help out with our honeymoon",
      image: beachImage,
      url: "https://www.honeyfund.com/site/schwartz-pochapin-10-18-2026?no_gdpr=1",
      backgroundColor: "#fff",
      isBeachImage: true
    }
  ];

  return (
    <PageContainer>
      <Title>Gift Registry</Title>
      <Description>
      Your presence at our wedding means the world to us and is truly the greatest gift.
      We've created a registry for those who wish to help us start this next chapter.
      Thank you so much for your love and support!
      </Description>
      
      <RegistryGrid>
        {registries.map((registry, index) => (
          <RegistryCard 
            key={index} 
            href={registry.url}
            target="_blank"
            rel="noopener noreferrer"
          >
            <RegistryImage noPadding={registry.isBeachImage} coverImage={registry.isBeachImage}>
              <img src={registry.image} alt={registry.name} />
            </RegistryImage>
            <RegistryInfo>
              <RegistryName>{registry.name}</RegistryName>
              <RegistryDescription>{registry.description}</RegistryDescription>
            </RegistryInfo>
          </RegistryCard>
        ))}
      </RegistryGrid>

      <CustomRegistrySection>
        <Title>Additional Gift Ideas</Title>
        <Description>
        We've also included a few ideas for meaningful gifts that aren't part of our official registry. If you'd like to add a personal touch or get creative, we hope these suggestions help inspire you.
        </Description>
        
        <CustomRegistryGrid>
          {customItems.map((item) => (
            <CustomRegistryItem
              key={item.id}
              purchased={item.purchased}
              onClick={() => {
                setSelectedItem(item);
                setIsModalOpen(true);
              }}
            >
              <ItemTitle>{item.title}</ItemTitle>
            </CustomRegistryItem>
          ))}
        </CustomRegistryGrid>
      </CustomRegistrySection>

      {isModalOpen && selectedItem && (
        <PurchaseModal
          item={selectedItem}
          onClose={() => {
            setIsModalOpen(false);
            setSelectedItem(null);
          }}
          onPurchase={handlePurchase}
        />
      )}
    </PageContainer>
  );
};

export default Registry; 